import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import type { GoalDetail, UpdateGoalInput } from '@pms/shared';
import { updateGoalSchema } from '@pms/shared';
import { goalsService } from '@/services/goals.service';
import { getErrorMessage } from '@/lib/errors';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ALL_GOAL_PRIORITIES,
  GOAL_PRIORITY_META,
  GOAL_TARGET_TYPE_META,
  NONE_OPTION,
} from '@/components/goals/goalConstants';

interface Props {
  goal: GoalDetail | null;
  onClose: () => void;
}

const numberOrNull = { setValueAs: (v: unknown) => (v === '' || v === null || v === undefined ? null : Number(v)) };

/**
 * Edits the definition of an existing goal. The target *type* is deliberately
 * fixed here — changing how a goal is measured after progress exists would
 * invalidate its history, so that needs a new goal.
 */
export function EditGoalModal({ goal, onClose }: Props) {
  const queryClient = useQueryClient();

  const { data: categories = [] } = useQuery({
    queryKey: ['goals', 'categories'],
    queryFn: () => goalsService.listCategories(),
    enabled: !!goal,
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<UpdateGoalInput>({
    resolver: zodResolver(updateGoalSchema),
  });

  useEffect(() => {
    if (!goal) return;
    reset({
      category_id: goal.category_id,
      title: goal.title,
      description: goal.description,
      start_value: goal.start_value,
      target_value: goal.target_value,
      unit: goal.unit,
      direction: goal.direction,
      weight: goal.weight,
      priority: goal.priority,
      start_date: goal.start_date,
      due_date: goal.due_date,
    });
  }, [goal, reset]);

  const startDate = watch('start_date');

  const mutation = useMutation({
    mutationFn: (data: UpdateGoalInput) => goalsService.updateGoal(goal!.id, data),
    onSuccess: () => {
      toast.success('Goal updated');
      void queryClient.invalidateQueries({ queryKey: ['goals'] });
      onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const busy = isSubmitting || mutation.isPending;
  const isMilestone = goal?.target_type === 'milestone';

  return (
    <Dialog open={!!goal} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Goal</DialogTitle>
          <DialogDescription>
            {goal ? `Measured as ${GOAL_TARGET_TYPE_META[goal.target_type].label.toLowerCase()} — the target type cannot be changed.` : ''}
          </DialogDescription>
        </DialogHeader>

        {goal && (
          <form onSubmit={handleSubmit((data) => mutation.mutate(data))} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="edit-goal-title">
                Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="edit-goal-title"
                className={errors.title ? 'border-destructive' : ''}
                {...register('title')}
              />
              {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
            </div>

            <div className="space-y-1">
              <Label htmlFor="edit-goal-description">Description</Label>
              <Textarea id="edit-goal-description" rows={3} {...register('description')} />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Category</Label>
                <Controller
                  control={control}
                  name="category_id"
                  render={({ field }) => (
                    <Select
                      value={field.value ?? NONE_OPTION}
                      onValueChange={(v) => field.onChange(v === NONE_OPTION ? null : v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Uncategorized" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE_OPTION}>Uncategorized</SelectItem>
                        {categories.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className="space-y-1">
                <Label>Priority</Label>
                <Controller
                  control={control}
                  name="priority"
                  render={({ field }) => (
                    <Select value={field.value ?? 'medium'} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ALL_GOAL_PRIORITIES.map((p) => (
                          <SelectItem key={p} value={p}>{GOAL_PRIORITY_META[p].label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>

            {!isMilestone && (
              <div className="grid grid-cols-1 gap-4 rounded-lg border border-border/60 bg-muted/20 p-3 sm:grid-cols-2">
                <div className="space-y-1 sm:col-span-2">
                  <Label>Direction</Label>
                  <Controller
                    control={control}
                    name="direction"
                    render={({ field }) => (
                      <Select value={field.value ?? 'increase'} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="increase">Increase — grow towards the target</SelectItem>
                          <SelectItem value="decrease">Decrease — bring the number down</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="edit-goal-start-value">Starting value</Label>
                  <Input
                    id="edit-goal-start-value"
                    type="number"
                    step="any"
                    className={errors.start_value ? 'border-destructive' : ''}
                    {...register('start_value', {
                      setValueAs: (v: unknown) => (v === '' || v === null || v === undefined ? 0 : Number(v)),
                    })}
                  />
                  {errors.start_value && <p className="text-xs text-destructive">{errors.start_value.message}</p>}
                </div>

                <div className="space-y-1">
                  <Label htmlFor="edit-goal-target-value">
                    Target value <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="edit-goal-target-value"
                    type="number"
                    step="any"
                    className={errors.target_value ? 'border-destructive' : ''}
                    {...register('target_value', numberOrNull)}
                  />
                  {errors.target_value && <p className="text-xs text-destructive">{errors.target_value.message}</p>}
                </div>

                {goal.target_type === 'numeric' && (
                  <div className="space-y-1 sm:col-span-2">
                    <Label htmlFor="edit-goal-unit">Unit</Label>
                    <Input id="edit-goal-unit" {...register('unit')} />
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Start date</Label>
                <Controller
                  control={control}
                  name="start_date"
                  render={({ field }) => (
                    <DatePicker value={field.value ?? ''} onChange={field.onChange} placeholder="Start date" />
                  )}
                />
                {errors.start_date && <p className="text-xs text-destructive">{errors.start_date.message}</p>}
              </div>

              <div className="space-y-1">
                <Label>Due date</Label>
                <Controller
                  control={control}
                  name="due_date"
                  render={({ field }) => (
                    <DatePicker
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      placeholder="Due date"
                      minDate={startDate ? new Date(`${startDate}T00:00:00`) : undefined}
                    />
                  )}
                />
                {errors.due_date && <p className="text-xs text-destructive">{errors.due_date.message}</p>}
              </div>

              <div className="space-y-1">
                <Label htmlFor="edit-goal-weight">Weight</Label>
                <Input
                  id="edit-goal-weight"
                  type="number"
                  min={1}
                  max={100}
                  {...register('weight', {
                    setValueAs: (v: unknown) => (v === '' || v === null || v === undefined ? 100 : Number(v)),
                  })}
                />
                {errors.weight && <p className="text-xs text-destructive">{errors.weight.message}</p>}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
